<template>
  <div class="reply-item">
    <!-- 头部 -->
    <div class="reply-header">
      <Avatar
        class="avatar"
        :avatar-index="props.notification.author_info?.avatar"
        size="medium"
        :clickable="!isSystemNotice"
        :is-system-avatar="isSystemNotice"
        :enable-navigate="!isSystemNotice"
        :user-id="props.notification.author_info?.uid"
        :avatar-frame="props.notification.author_info?.avatar_frame"
      />
      <div class="user-info">
        <span
          class="notice-title"
          :style="isSystemNotice ? '' : 'cursor:pointer;'"
          @click.stop="!isSystemNotice && goUserPage()"
        >
          {{ noticeTitle }}
        </span>
        <span class="dateline">{{ notification.dateline_formatted }}</span>
      </div>
    </div>
    <!-- 内容区 -->
    <template v-if="isSystemNotice">
      <div class="reply-content" v-html="notification.content"></div>
    </template>
    <template v-else>
      <!-- 回复内容 -->
      <div
        class="reply-content"
        v-html="decodeHtml(notification.detail?.post_content)"
      ></div>
      <!-- 回复输入框 -->
      <div class="reply-input" @click="onReplyClick">
        <span class="notify-user" v-if="notification.type == NoticeTab.Reply">
          {{ notification.notify_user?.username }}：
        </span>
        <span
          class="content"
          v-html="
            decodeHtml(
              notification.detail?.parent_post_content ||
                notification.detail?.thread_content ||
                notification.detail?.post_content ||
                notification.detail?.subject
            )
          "
        ></span>
      </div>
      <!-- 底部操作 -->
      <div
        class="reply-actions"
        v-if="[NoticeTab.Reply, NoticeTab.Mention].includes(type)"
      >
        <div class="action comment" @click="onReplyClick">
          <span class="icon comment-icon"></span>
        </div>
        <div class="action like" @click="handleLike">
          <span class="icon like-icon" :class="{ 'is-active': isLiked }"></span>
          <span>{{ likeCount }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { decodeHtml } from "../../utils";
import { NoticeTab } from "@/stores/user";
import { useI18n } from "vue-i18n";
import { useUserStore, useToastStore } from "@/stores";
import { useLikeStore } from "@/stores/like";
import Avatar from "../common/Avatar.vue";

const props = defineProps({
  notification: {
    type: Object,
    required: true,
  },
  type: {
    type: [String, Number],
    required: false,
  },
});

const emit = defineEmits<{
  (e: "update:notification", value: any): void;
}>();

const { t } = useI18n();
const toast = useToastStore();
const likeStore = useLikeStore();
const userStore = useUserStore();

// 点赞状态管理
const isLiked = computed(() => props.notification.detail?.is_liked || false);
const likeCount = computed(() => props.notification.detail?.likes || 0);

const isSystemNotice = computed(() => {
  return (
    props.type === NoticeTab.Notification ||
    props.notification.type === "system"
  );
});

// 多语言key选择
const noticeTitle = computed(() => {
  if (isSystemNotice.value) return t("notice.system");
  // 只处理reply/mention/like三类
  let key = "";
  if (props.type === NoticeTab.Reply) {
    key =
      props.notification.type === "post"
        ? "notice.replyComment"
        : "notice.replyPost";
  } else if (props.type === NoticeTab.Mention) {
    key =
      props.notification.type === "post"
        ? "notice.mentionComment"
        : "notice.mentionPost";
  } else if (props.type === NoticeTab.Like) {
    key =
      props.notification.type === "post"
        ? "notice.likeComment"
        : "notice.likePost";
  }
  return key
    ? t(key, { username: props.notification.author_info?.username })
    : "";
});

const router = useRouter();

const goUserPage = () => {
  router.push(`/user/${props.notification.author_info.uid}`);
};

const onReplyClick = () => {
  router.push({
    path: `/post/${props.notification.detail.tid}`,
    query: {
      replyFloor: props.notification.detail.pid,
    },
  });
};

const handleLike = async () => {
  if (!userStore.checkLogin()) {
    return;
  }

  const pid = props.notification.detail?.pid;
  const tid = props.notification.detail?.tid;
  const isPost =
    props.notification.type === "post" || props.type === NoticeTab.Reply;
  if (!pid && !tid) {
    return;
  }

  if (isLiked.value) {
    const res = await likeStore.cancelLike(
      isPost ? pid : tid,
      isPost ? "post" : "thread"
    );
    if (res?.code == 200) {
      // 更新通知对象中的点赞状态
      const updatedNotification = {
        ...props.notification,
        detail: {
          ...props.notification.detail,
          is_liked: false,
          likes: Math.max(0, (+props.notification.detail?.likes || 0) - 1),
        },
      };
      emit("update:notification", updatedNotification);
      toast.showToast(t("profile.cancelLikeSuccess"), "success", 1000);
    }
  } else {
    const res = await likeStore.addLike(
      isPost ? pid : tid,
      isPost ? "post" : "thread"
    );
    if (res?.code == 200) {
      // 更新通知对象中的点赞状态
      const updatedNotification = {
        ...props.notification,
        detail: {
          ...props.notification.detail,
          is_liked: true,
          likes: (+props.notification.detail?.likes || 0) + 1,
        },
      };
      emit("update:notification", updatedNotification);
      toast.showToast(t("profile.addLikeSuccess"), "success", 1000);
    }
  }
};
</script>

<style scoped>
.reply-item {
  background: #fff;
  border-radius: 15px;
  padding: 16px;
  margin-bottom: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
.reply-header {
  display: flex;
  align-items: center;
}
.avatar {
  margin-right: 10px;
  cursor: pointer;
}
.user-info {
  display: flex;
  flex-direction: column;
}
.notice-title {
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
}
.notify-user {
  font-weight: 600;
  font-size: 14px;
  color: #f6c64b;
}
.dateline {
  color: #aaa;
  font-size: 12px;
}
.reply-content {
  margin: 15px 0;
  font-size: 15px;
  color: #222;
}

.reply-input {
  background: #f5f5f5;
  color: #bbb;
  border-radius: 8px;
  padding: 8px 16px;
  margin-bottom: 10px;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}
.reply-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 24px;
}
.action {
  display: flex;
  align-items: center;
  color: #999;
  font-size: 15px;
  cursor: pointer;
}
.comment-icon {
  width: 18px;
  height: 18px;
  background: url("../../assets/comment.png") no-repeat center/contain;
  margin-right: 4px;
}
.like-icon {
  width: 18px;
  height: 18px;
  background: url("../../assets/like.png") no-repeat center/contain;
  margin-right: 4px;
  cursor: pointer;
}

.like-icon.is-active {
  background-image: url("../../assets/like-active.png");
}
</style>
